import { Component, OnInit } from '@angular/core';
import { ApiService } from '../services/api.service';
import { Employee } from '../model/employee.model';
import { CustomerBills } from '../model/customerbills.model';
import { AreaData } from '../model/area.model';
import { MatSnackBar } from '@angular/material/snack-bar';
import { FormControl } from '@angular/forms';
import { forkJoin } from 'rxjs';
import * as XLSX from 'xlsx';

interface Cycle {
  label: string;
  startDate: string;
  endDate: string;
}

interface BillCounts {
  all: number;
  billed: number;
  unbilled: number;
  paid: number;
  unpaid: number;
  danguud: number;
}

interface BillColumn {
  key: string;
  label: string;
  type: 'text' | 'currency';
  visible: boolean;
}

@Component({
  selector: 'app-view-bill-info',
  templateUrl: './view-bill-info.component.html',
  styleUrls: ['./view-bill-info.component.scss']
})
export class ViewBillInfoComponent implements OnInit {
  availableCycles: Cycle[] = [];
  selectedCycleControl = new FormControl('');
  currentCycleStart: string = '';
  currentCycleEnd: string = '';

  employees: Employee[] = [];
  collectorEmployees: Employee[] = [];
  selectedEmployeeControl = new FormControl(null);

  availableAreas: AreaData[] = [];
  selectedAreaControl = new FormControl(null);
  selectedAreaCodeControl = new FormControl(null);

  // Search mode: predefined cycle or custom calendar range
  searchMode: 'cycle' | 'range' = 'cycle';
  rangeStart = new FormControl(null);
  rangeEnd = new FormControl(null);
  maxDate: Date = new Date();

  billsByCategory: { [key: string]: CustomerBills[] } = {
    all: [],
    billed: [],
    unbilled: [],
    paid: [],
    unpaid: [],
    danguud: []
  };
  filteredBills: CustomerBills[] = [];
  loading: boolean = false;
  showResults: boolean = false;

  activeTab: string = 'all';
  activeTabIndex: number = 0;
  tabLabels: string[] = ['all', 'billed', 'unbilled', 'paid', 'unpaid', 'danguud'];

  counts: BillCounts = {
    all: 0,
    billed: 0,
    unbilled: 0,
    paid: 0,
    unpaid: 0,
    danguud: 0
  };

  columns: BillColumn[] = [
    { key: 'employeeId', label: 'Emp ID', type: 'text', visible: true },
    { key: 'employeeName', label: 'Employee', type: 'text', visible: true },
    { key: 'customerId', label: 'Customer ID', type: 'text', visible: true },
    { key: 'customerName', label: 'Customer Name', type: 'text', visible: true },
    { key: 'customerPhone', label: 'Phone', type: 'text', visible: true },
    { key: 'customerAddress', label: 'Address', type: 'text', visible: true },
    { key: 'billDate', label: 'Bill Date', type: 'text', visible: true },
    { key: 'previousReading', label: 'Prev Reading', type: 'text', visible: true },
    { key: 'currentReading', label: 'Current Reading', type: 'text', visible: true },
    { key: 'unitsConsumed', label: 'Units', type: 'text', visible: true },
    { key: 'diff_in_last_month_units_consumed', label: 'Farqi m³', type: 'text', visible: true },
    { key: 'billAmount', label: 'Bill Amount', type: 'currency', visible: true },
    { key: 'amountPaid', label: 'Paid', type: 'currency', visible: true },
    { key: 'prevMonthOutstandingBal', label: 'RH', type: 'currency', visible: true },
    { key: 'totalOutstandingBal', label: 'RC', type: 'currency', visible: true },
    { key: 'wadarta', label: 'Wadarta', type: 'currency', visible: true },
    { key: 'bad_debt', label: 'Daan Guud', type: 'currency', visible: true },
    { key: 'discount', label: 'Discount', type: 'currency', visible: true },
    { key: 'billGeneratedFlag', label: 'Bill Generated', type: 'text', visible: true },
    { key: 'bill_remarks', label: 'Bill Remarks', type: 'text', visible: true },
    { key: 'billPaidDate', label: 'Bill Paid Date', type: 'text', visible: true }
  ];

  constructor(
    private apiService: ApiService,
    private snackBar: MatSnackBar
  ) { }

  ngOnInit(): void {
    this.initializeAvailableCycles();
    this.loadEmployees();
    this.watchEmployeeAndArea();
  }

  private watchEmployeeAndArea(): void {
    this.selectedEmployeeControl.valueChanges.subscribe(employeeId => {
      this.selectedAreaControl.setValue(null);
      this.selectedAreaCodeControl.setValue(null);
      this.availableAreas = [];
      if (!employeeId || !this.collectorEmployees.length) {
        return;
      }
      const emp = this.collectorEmployees.find(e => e.employeeId === Number(employeeId));
      if (emp && emp.areas && emp.areas.length) {
        this.availableAreas = emp.areas;
      } else {
        this.apiService.getEmployeeDetailsById(employeeId).subscribe((response: any) => {
          const employee = response && response.employee ? response.employee : response;
          this.availableAreas = employee && employee.areas ? employee.areas : [];
        });
      }
    });

    this.selectedAreaControl.valueChanges.subscribe(() => {
      this.showActiveTabData();
    });

    this.selectedAreaCodeControl.valueChanges.subscribe(() => {
      this.showActiveTabData();
    });
  }

  private initializeAvailableCycles(): void {
    const today = new Date();
    const cycles: Cycle[] = [];
    const seenLabels = new Set<string>();

    for (let i = 0; i < 12; i++) {
      const cycleDate = new Date(today.getFullYear(), today.getMonth() - i, 15);
      let cycleStart = new Date(cycleDate.getFullYear(), cycleDate.getMonth(), 25);
      let cycleEnd = new Date(cycleDate.getFullYear(), cycleDate.getMonth() + 1, 24);

      if (cycleStart > today) {
        cycleStart.setMonth(cycleStart.getMonth() - 1);
        cycleEnd.setMonth(cycleEnd.getMonth() - 1);
      }

      const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
      const label = `${monthNames[cycleStart.getMonth()]} ${cycleStart.getFullYear()} Cycle`;

      if (seenLabels.has(label)) {
        continue;
      }
      seenLabels.add(label);

      cycles.push({
        label,
        startDate: this.formatDate(cycleStart),
        endDate: this.formatDate(cycleEnd)
      });
    }

    this.availableCycles = cycles;

    if (cycles.length > 0) {
      this.selectedCycleControl.setValue(cycles[0].label);
      this.currentCycleStart = cycles[0].startDate;
      this.currentCycleEnd = cycles[0].endDate;
      this.rangeStart.setValue(this.parseLocalDate(cycles[0].startDate));
      this.rangeEnd.setValue(this.parseLocalDate(cycles[0].endDate));
    }
  }

  onCycleChange(selectedLabel: string): void {
    const selectedCycle = this.availableCycles.find(cycle => cycle.label === selectedLabel);
    if (selectedCycle) {
      this.selectedCycleControl.setValue(selectedLabel);
      this.currentCycleStart = selectedCycle.startDate;
      this.currentCycleEnd = selectedCycle.endDate;
      // Keep the calendar range in sync so switching modes is seamless
      this.rangeStart.setValue(this.parseLocalDate(selectedCycle.startDate));
      this.rangeEnd.setValue(this.parseLocalDate(selectedCycle.endDate));
    }
  }

  setSearchMode(mode: string): void {
    this.searchMode = mode === 'range' ? 'range' : 'cycle';
  }

  private parseLocalDate(value: string): Date {
    const [year, month, day] = value.split('-').map(Number);
    return new Date(year, month - 1, day);
  }

  private loadEmployees(): void {
    this.loading = true;
    this.apiService.getEmployees().subscribe({
      next: (response: any) => {
        this.employees = response.employees || [];
        this.collectorEmployees = this.employees.filter(emp =>
          emp.roles && emp.roles.some(role => role.roleName === 'collector')
        );
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading employees:', error);
        this.loading = false;
        this.snackBar.open('Error loading employees', 'Close', { duration: 3000 });
      }
    });
  }

  searchBills(): void {
    this.selectedAreaControl.setValue(null);
    this.selectedAreaCodeControl.setValue(null);
    const selectedEmployeeId = this.selectedEmployeeControl.value;

    if (!selectedEmployeeId) {
      this.snackBar.open('Please select a collector employee', 'Close', { duration: 3000 });
      return;
    }

    let start: string;
    let end: string;

    if (this.searchMode === 'range') {
      const startDate = this.rangeStart.value;
      if (!startDate) {
        this.snackBar.open('Please select a start date', 'Close', { duration: 3000 });
        return;
      }
      // If no end date is chosen, treat it as a single day
      const endDate = this.rangeEnd.value || startDate;
      start = this.formatDate(new Date(startDate));
      end = this.formatDate(new Date(endDate));
    } else {
      const selectedCycle = this.availableCycles.find(cycle => cycle.label === this.selectedCycleControl.value);
      if (!selectedCycle) {
        this.snackBar.open('Please select a billing cycle', 'Close', { duration: 3000 });
        return;
      }
      start = selectedCycle.startDate;
      end = selectedCycle.endDate;
    }

    this.currentCycleStart = start;
    this.currentCycleEnd = end;

    this.loading = true;
    this.showResults = false;

    const empId = Number(selectedEmployeeId);

    // Separate API calls per category, executed in parallel
    forkJoin({
      all: this.apiService.getViewBillInfoAll(empId, start, end),
      billed: this.apiService.getViewBillInfoBilled(empId, start, end),
      unbilled: this.apiService.getViewBillInfoUnbilled(empId, start, end),
      paid: this.apiService.getViewBillInfoPaid(empId, start, end),
      unpaid: this.apiService.getViewBillInfoUnpaid(empId, start, end),
      danguud: this.apiService.getViewBillInfoDanGuud(empId, start, end)
    }).subscribe({
      next: (res: any) => {
        const nameById = new Map<number, string>();
        this.collectorEmployees.forEach(emp => nameById.set(emp.employeeId, emp.employeeName));
        const withNames = (bills: CustomerBills[]): CustomerBills[] =>
          (bills || []).map(bill => ({
            ...bill,
            employeeName: nameById.get(bill.employeeId) || `Employee ${bill.employeeId}`
          }));

        this.billsByCategory = {
          all: withNames(res.all?.customerBills),
          billed: withNames(res.billed?.customerBills),
          unbilled: withNames(res.unbilled?.customerBills),
          paid: withNames(res.paid?.customerBills),
          unpaid: withNames(res.unpaid?.customerBills),
          danguud: withNames(res.danguud?.customerBills)
        };
        this.updateCounts();
        this.showResults = true;
        this.showActiveTabData();
        this.loading = false;
      },
      error: (error) => {
        console.error('Error loading bill info:', error);
        this.loading = false;
        this.snackBar.open('Error loading bill information', 'Close', { duration: 3000 });
      }
    });
  }

  onTabIndexChange(index: number): void {
    this.activeTabIndex = index;
    this.activeTab = this.tabLabels[index];
    this.showActiveTabData();
  }

  private filterByArea(bills: CustomerBills[]): CustomerBills[] {
    const selectedArea = this.selectedAreaControl.value;
    const selectedAreaCode = this.selectedAreaCodeControl.value;

    return bills.filter(bill => {
      if (selectedArea && bill.areaName !== selectedArea) {
        return false;
      }
      if (selectedAreaCode != null) {
        const billAreaCode = bill.areaCode != null ? String(bill.areaCode) : '';
        if (billAreaCode !== String(selectedAreaCode)) {
          return false;
        }
      }
      return true;
    });
  }

  private updateCounts(): void {
    this.counts = {
      all: this.filterByArea(this.billsByCategory.all || []).length,
      billed: this.filterByArea(this.billsByCategory.billed || []).length,
      unbilled: this.filterByArea(this.billsByCategory.unbilled || []).length,
      paid: this.filterByArea(this.billsByCategory.paid || []).length,
      unpaid: this.filterByArea(this.billsByCategory.unpaid || []).length,
      danguud: this.filterByArea(this.billsByCategory.danguud || []).length
    };
  }

  private showActiveTabData(): void {
    this.updateCounts();
    this.filteredBills = this.filterByArea(this.billsByCategory[this.activeTab] || []);
  }

  getEmployeeName(employeeId: number): string {
    const employee = this.collectorEmployees.find(emp => emp.employeeId === employeeId);
    return employee ? employee.employeeName : `Employee ${employeeId}`;
  }

  trackByBill(index: number, bill: CustomerBills): number {
    return bill.customerId;
  }

  get visibleColumns(): BillColumn[] {
    return this.columns.filter(col => col.visible);
  }

  toggleColumn(col: BillColumn): void {
    col.visible = !col.visible;
  }

  isBillCreated(bill: CustomerBills): boolean {
    const flag = (bill.billGeneratedFlag || '').trim().toUpperCase();
    return flag === 'Y' || flag === 'YES' || flag === 'TRUE' || flag === '1';
  }

  // Total customers for the current filter (each row is a distinct customer)
  get totalCustomers(): number {
    return this.filteredBills.length;
  }

  // Customers whose bills are NOT created for the current filter
  get notCreatedBillsCount(): number {
    return this.filteredBills.filter(bill => !this.isBillCreated(bill)).length;
  }

  formatCell(bill: CustomerBills, col: BillColumn): string {
    const value = (bill as any)[col.key];
    if (col.type === 'currency') {
      return this.formatCurrency(value);
    }
    return value === null || value === undefined ? '' : String(value);
  }

  isNumericColumn(colKey: string): boolean {
    const numericColumns = [
      'previousReading', 'currentReading', 'unitsConsumed', 'diff_in_last_month_units_consumed',
      'billAmount', 'amountPaid', 'prevMonthOutstandingBal', 'totalOutstandingBal',
      'finalDueAmount', 'wadarta', 'bad_debt', 'discount'
    ];
    return numericColumns.includes(colKey);
  }

  getColumnTotal(col: BillColumn): string {
    // Only show totals for specific currency columns
    const totalColumns = ['billAmount', 'amountPaid', 'prevMonthOutstandingBal', 'totalOutstandingBal', 'wadarta', 'bad_debt', 'discount'];
    if (!totalColumns.includes(col.key)) {
      return '';
    }
    const total = this.filteredBills.reduce((sum, bill) => {
      const value = (bill as any)[col.key];
      return sum + (value != null && !isNaN(value) ? Number(value) : 0);
    }, 0);
    return this.formatCurrency(total);
  }

  exportToExcel(): void {
    if (!this.filteredBills.length) {
      this.snackBar.open('No data to export', 'Close', { duration: 3000 });
      return;
    }
    const cols = this.visibleColumns;
    const rows = this.filteredBills.map(bill => {
      const obj: { [key: string]: any } = {};
      cols.forEach(col => {
        const value = (bill as any)[col.key];
        if (col.type === 'currency') {
          // Keep currency values as raw numbers so Excel can sum them
          obj[col.label] = value != null && !isNaN(value) ? Number(value) : null;
        } else {
          obj[col.label] = value === null || value === undefined ? '' : value;
        }
      });
      return obj;
    });
    const worksheet = XLSX.utils.json_to_sheet(rows, { header: cols.map(c => c.label) });
    const workbook = XLSX.utils.book_new();
    const sheetName = this.activeTab.toUpperCase().substring(0, 31);
    XLSX.utils.book_append_sheet(workbook, worksheet, sheetName);
    const fileName = `bill-info-${this.activeTab}-${this.currentCycleStart}.xlsx`;
    XLSX.writeFile(workbook, fileName);
  }

  exportToPDF(): void {
    if (!this.filteredBills.length) {
      this.snackBar.open('No data to export', 'Close', { duration: 3000 });
      return;
    }
    const cols = this.visibleColumns;
    const collectorName = this.getEmployeeName(Number(this.selectedEmployeeControl.value));
    const tabTitle = this.activeTab.toUpperCase();
    const generatedOn = new Date().toLocaleString();

    const headerCells = cols.map(col => `<th>${col.label}</th>`).join('');
    const bodyRows = this.filteredBills.map(bill => {
      const cells = cols.map(col => `<td>${this.escapeHtml(this.formatCell(bill, col))}</td>`).join('');
      return `<tr>${cells}</tr>`;
    }).join('');
    const totalCells = cols.map(col => `<td class="total-cell">${this.escapeHtml(this.getColumnTotal(col))}</td>`).join('');
    const totalRow = `<tr class="total-row">${totalCells}</tr>`;

    const html = `
      <html>
        <head>
          <title>Bill Info - ${tabTitle}</title>
          <style>
            * { box-sizing: border-box; }
            body { font-family: 'Segoe UI', Arial, sans-serif; color: #1a2332; margin: 24px; }
            .doc-header { display: flex; justify-content: space-between; align-items: flex-start;
              border-bottom: 3px solid #00bfa5; padding-bottom: 12px; margin-bottom: 16px; }
            .doc-header .brand { font-size: 22px; font-weight: 700; color: #00bfa5; }
            .doc-header .brand small { display: block; font-size: 11px; font-weight: 400; color: #607089; }
            .doc-meta { text-align: right; font-size: 12px; color: #445; }
            .doc-title { font-size: 16px; font-weight: 600; margin: 4px 0 12px; }
            .summary { display: flex; gap: 24px; margin-bottom: 14px; font-size: 12px; }
            .summary .chip { background: #eef4fb; border: 1px solid #cfe0f2; border-radius: 6px;
              padding: 6px 12px; }
            .summary .chip b { color: #00bfa5; }
            table { width: 100%; border-collapse: collapse; font-size: 10px; }
            thead th { background: #00bfa5; color: #fff; text-align: left; padding: 6px 8px;
              border: 1px solid #00bfa5; }
            tbody td { padding: 5px 8px; border: 1px solid #d5dde8; }
            tbody tr:nth-child(even) { background: #f5f8fc; }
            tfoot.total-row td { font-weight: 700; background: #e8f0fa; border: 1px solid #b8cbe0;
              padding: 5px 8px; text-align: left; }
            .foot { margin-top: 16px; font-size: 10px; color: #889; text-align: center; }
            @media print { body { margin: 10mm; } }
          </style>
        </head>
        <body>
          <div class="doc-header">
            <div class="brand">Xamardaye<small>Bill Information Report</small></div>
            <div class="doc-meta">
              <div><b>Generated:</b> ${generatedOn}</div>
              <div><b>Cycle:</b> ${this.currentCycleStart} to ${this.currentCycleEnd}</div>
              <div><b>Collector:</b> ${this.escapeHtml(collectorName)}</div>
            </div>
          </div>
          <div class="doc-title">Category: ${tabTitle}</div>
          <div class="summary">
            <div class="chip">Total Customers: <b>${this.totalCustomers}</b></div>
            <div class="chip">Not Created Bills: <b>${this.notCreatedBillsCount}</b></div>
          </div>
          <table>
            <thead><tr>${headerCells}</tr></thead>
            <tbody>${bodyRows}</tbody>
            <tfoot class="total-row">${totalRow}</tfoot>
          </table>
          <div class="foot">Xamardaye &mdash; Confidential. Generated from View Bill Info.</div>
        </body>
      </html>`;

    const printWindow = window.open('', '_blank');
    if (!printWindow) {
      this.snackBar.open('Please allow pop-ups to export PDF', 'Close', { duration: 4000 });
      return;
    }
    printWindow.document.open();
    printWindow.document.write(html);
    printWindow.document.close();
    printWindow.focus();
    setTimeout(() => {
      printWindow.print();
    }, 300);
  }

  private escapeHtml(value: string): string {
    return (value || '')
      .replace(/&/g, '&amp;')
      .replace(/</g, '&lt;')
      .replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;')
      .replace(/'/g, '&#039;');
  }

  private formatDate(date: Date): string {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
  }

  formatCurrency(amount: number): string {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD'
    }).format(amount || 0);
  }
}
